import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ActionDropdown } from '@/components';
import { TableData, type TableDataColumn } from '@/design-system/table-data';

import { warehouseApi } from '../../api/warehouse.api';
import { EmptyTable, ListSearch, ListShell } from '../../components/ListShell';
import { PoSourceCell, PoTypeTag } from '../../components/orderType';
import { HeaderActions } from '../../components/pickers';
import {
  codeCls,
  dateCls,
  LoadError,
  MappedBadge,
  nameCls,
  PageHeader,
  ProgressCell,
} from '../../components/ui';
import { usePurchaseOrderActions } from '../../components/usePurchaseOrderActions';
import { fmtDateTime, fmtQty } from '../../format';
import { WarehouseFilterPanel } from '../../components/WarehouseFilterPanel';
import { INBOUND_FILTERS } from '../../filterFields';
import { useListQuery } from '../../hooks/useListQuery';
import { useLoad } from '../../hooks/useLoad';
import { RECEIPT_STATUS, RECEIPT_TABS } from '../../status';
import type { PurchaseOrder, PurchaseOrderReceiptStatus } from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { WAREHOUSE_PATHS } from '../../paths';

type Tab = (typeof RECEIPT_TABS)[number];

const InboundRequestListPage: React.FC = () => {
  const navigate = useNavigate();
  const { hubID, ready, refreshToken } = useWarehouse();
  const q = useListQuery<Tab>(RECEIPT_TABS, 'ALL', INBOUND_FILTERS);
  const { api: filterApi, apiKey: filterKey } = q.filters;
  const search = q.search.committed || null;

  const list = useLoad(
    (signal) =>
      warehouseApi.listInboundRequests(
        {
          hubID,
          receiptStatus:
            q.tab === 'ALL' ? null : (q.tab as PurchaseOrderReceiptStatus),
          ...filterApi,
          search,
          page: q.page,
          limit: q.limit,
        },
        { signal }
      ),
    [hubID, q.tab, filterKey, search, q.page, q.limit, refreshToken],
    ready
  );
  // Tab counts: the list's filters (minus the tab) — never disagree with it.
  const summary = useLoad(
    (signal) =>
      warehouseApi.getInboundRequestSummary(
        { hubID, ...filterApi, search },
        { signal }
      ),
    [hubID, filterKey, search, refreshToken],
    ready
  );

  // After a cancel: reload what this page shows.
  const reloadList = list.reload;
  const reloadSummary = summary.reload;
  const onChanged = useCallback(() => {
    reloadList();
    reloadSummary();
  }, [reloadList, reloadSummary]);
  const actions = usePurchaseOrderActions(onChanged, { as: 'inbound' });
  const { requestCancel, canCancel } = actions;

  const tabs = RECEIPT_TABS.map((k) => ({
    key: k,
    label: k === 'ALL' ? 'All' : RECEIPT_STATUS[k].label,
    count: summary.data
      ? k === 'ALL'
        ? summary.data.total
        : (summary.data.byStatus.find((b) => b.status === k)?.count ?? 0)
      : null,
  }));

  const columns = useMemo<TableDataColumn<PurchaseOrder>[]>(
    () => [
      {
        key: 'code',
        title: 'PO number',
        render: (_v, x) => <span className={codeCls}>{x.code}</span>,
      },
      {
        key: 'client',
        title: 'Client',
        render: (_v, x) => (
          <span className={nameCls}>
            {x.client?.name ?? `Client #${x.clientID}`}
          </span>
        ),
      },
      {
        key: 'type',
        title: 'Tipe',
        render: (_v, x) => <PoTypeTag type={x.type} />,
      },
      {
        key: 'source',
        title: 'Sumber',
        render: (_v, x) => <PoSourceCell po={x} />,
      },
      {
        key: 'confirmedAt',
        title: 'Date created',
        render: (_v, x) => (
          <span className={dateCls}>
            {fmtDateTime(x.confirmedAt ?? x.createdAt)}
          </span>
        ),
      },
      {
        key: 'eta',
        title: 'Est. arrival',
        render: (_v, x) => (
          <span className={dateCls}>{fmtDateTime(x.estArrivalAt)}</span>
        ),
      },
      {
        key: 'hub',
        title: 'Hub',
        render: (_v, x) => (
          <span className={nameCls}>{x.hub?.name ?? `Hub #${x.hubID}`}</span>
        ),
      },
      {
        key: 'received',
        title: 'Received',
        render: (_v, x) => (
          <ProgressCell
            received={x.totalReceived}
            expected={x.totalQty}
            format={fmtQty}
          />
        ),
      },
      {
        key: 'irStatus',
        title: 'Status',
        render: (_v, x) => (
          <MappedBadge map={RECEIPT_STATUS} value={x.receiptStatus} />
        ),
      },
      {
        key: 'actions',
        title: '',
        align: 'right',
        render: (_v, x) => (
          <ActionDropdown
            actions={[
              {
                key: 'view',
                label: 'View details',
                icon: 'ri-eye-line',
                onClick: () =>
                  navigate(`${WAREHOUSE_PATHS.inboundRequest}/${x.id}`),
              },
              {
                key: 'gr',
                label: 'Buat goods receipt',
                icon: 'ri-inbox-archive-line',
                hidden: x.receiptStatus === 'FINISHED',
                onClick: () =>
                  navigate(`${WAREHOUSE_PATHS.goodsReceipt}/new?po=${x.id}`),
              },
              {
                key: 'po',
                label: 'Buka purchase order',
                icon: 'ri-file-list-3-line',
                onClick: () =>
                  navigate(`${WAREHOUSE_PATHS.purchaseOrder}/${x.id}`),
              },
              {
                key: 'cancel',
                label: 'Batalkan',
                icon: 'ri-close-circle-line',
                destructive: true,
                hidden: !canCancel(x),
                onClick: () => requestCancel(x),
              },
            ]}
          />
        ),
      },
    ],
    [navigate, canCancel, requestCancel]
  );

  return (
    <div className="space-y-5 p-6">
      <PageHeader
        eyebrow="Warehouse · Inbound management"
        title="Inbound request"
        actions={<HeaderActions />}
      />
      <ListShell
        tabs={tabs}
        activeTab={q.tab}
        onTabChange={q.setTab}
        filters={
          <>
            <ListSearch
              value={q.search.value}
              onChange={q.search.setValue}
              placeholder="Search PO, vendor…"
            />
            <WarehouseFilterPanel
              filters={q.filters}
              title="Filter inbound request"
            />
          </>
        }
        pagination={{
          page: q.page,
          limit: q.limit,
          total: list.data?.pagination.total ?? 0,
          onPageChange: q.setPage,
          onLimitChange: q.setLimit,
        }}
      >
        {list.error ? (
          <LoadError message={list.error.message} onRetry={list.reload} />
        ) : (
          <TableData
            size="sm"
            loading={list.loading || !ready}
            data={list.data?.data ?? []}
            columns={columns}
            onRowClick={(x) =>
              navigate(`${WAREHOUSE_PATHS.inboundRequest}/${x.id}`)
            }
            emptyState={<EmptyTable title="Tidak ada inbound request" />}
          />
        )}
      </ListShell>
      {actions.dialogs}
    </div>
  );
};

export default InboundRequestListPage;
